import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail } from "lucide-react";
import { useAuth } from "../auth/AuthContext.jsx";
import bgImg from "../assets/bg.png";
import "../styles/tailwind.css";

// The login background (bg.png) already contains all the branding and dashboard
// illustration — so the only thing rendered on top is the form card.
export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  useEffect(() => { document.title = "TangoEye - Login"; }, []);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (loading) return;
    if (!email.trim() || !password.trim()) {
      setError("Please enter your email and password.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      // login runs the real login + profile + client stores flow and resolves
      // when the dashboard is ready, or throws on any failure.
      await login({ email, password });
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full bg-cover bg-center bg-no-repeat text-stone-900 antialiased" style={{ backgroundImage: `url(${bgImg})` }}>
      <div className="mx-auto flex min-h-screen max-w-[1440px] items-center justify-center px-6 py-10 lg:justify-end lg:px-12">
        <div className="w-full max-w-[560px] flex-shrink-0">
          <div className="rounded-3xl bg-white p-8 shadow-[0_30px_70px_-25px_rgba(40,60,120,0.35)] sm:p-10">
            <h1 className="text-[34px] font-bold tracking-tight text-stone-900">Log in</h1>
            <p className="mt-1.5 text-[14px] text-stone-500">Welcome back! Please enter your details.</p>

            <form onSubmit={submit} className="mt-7 space-y-5">
              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-stone-700">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="username"
                  className="w-full rounded-xl border border-stone-200 bg-[#eef1f9] px-4 py-3 text-[14px] text-stone-800 placeholder:text-stone-400 focus:border-[#1898D8] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1898D8]/20"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-[13px] font-semibold text-stone-700">Password</label>
                <div className="relative">
                  <input
                    type={showPw ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="w-full rounded-xl border border-stone-200 bg-[#eef1f9] px-4 py-3 pr-11 text-[14px] text-stone-800 placeholder:text-stone-400 focus:border-[#1898D8] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#1898D8]/20"
                  />
                  <button type="button" onClick={() => setShowPw((s) => !s)} aria-label={showPw ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-lg text-stone-400 hover:bg-stone-100 hover:text-stone-600">
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-center text-[13px] font-medium text-red-600">{error}</p>}

              <button type="submit" disabled={loading}
                className="w-full rounded-xl bg-[#1898D8] py-3 text-[15px] font-semibold text-white shadow-sm transition hover:bg-[#1280ba] disabled:cursor-not-allowed disabled:opacity-60">
                {loading ? "Signing in…" : "Sign In"}
              </button>

              <div className="text-center">
                <a
                  href="https://dashboard.tangoeye.ai/auth/forgot-password"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] font-medium text-[#1898D8] underline-offset-2 hover:underline"
                >
                  Forgot password?
                </a>
              </div>
            </form>

            <div className="mt-8 flex flex-wrap items-center justify-between gap-2 text-[11px] text-stone-400">
              <span>2024 <a href="#" className="underline-offset-2 hover:underline">@Tango IT Solutions India Pvt Ltd</a></span>
              <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />support@tangotech.co.in</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
